import React from 'react';
import { Btn, Loading, Msg, Section, fmtCell, fmtDate, useAdminGet } from '../kit';
import { Pill, num, str } from '../ipintel/shared';
import {
  ConfidencePill, Drawer, Fact, type HzCtx, HorizonPill, LeverChips, S, TextBlock, appsOf, fmtCost, leversOf,
  promptLabel, publicUrl, shortId,
} from './shared';

/**
 * One result, every column — GET horizon/result {id}: the full row
 * including the raw model response, plus the prompt and run it belongs to.
 * Opens over the technology drawer; Escape closes this one first.
 */

/** Columns shown in their own place below; everything else lands in "Other columns". */
const SHOWN = new Set([
  'id', 'run_id', 'prompt_id', 'utech_technology_id', 'tech_title', 'university_name', 'inferred_problem', 'horizon',
  'horizon_confidence', 'primary_problem_type', 'secondary_problem_types', 'reasoning', 'example_applications',
  'raw_response', 'tokens', 'latency_ms', 'cost', 'error', 'reviewed', 'correct_horizon', 'correct_problem_type',
  'corrected_horizon', 'corrected_problem_type', 'approved', 'review_notes', 'created_at', 'reviewed_at', 'industry',
  'value_lever_tags', 'tags_to_add', 'tags_to_remove', 'correct_tags', 'prompt', 'run', 'levers', 'applications',
  // admin_horizon.result's own additions, shown in the header / facts
  'tech_id', 'university_code', 'prompt_name', 'ok', 'is_current', 'tech_page_path', 'tech_in_corpus',
]);

const yn = (v: any) => (v === null || v === undefined || v === '' ? '—' : v === true || v === 't' || v === 1 || v === '1' || v === 'true' ? 'Yes' : 'No');

const ResultDrawer: React.FC<{ id: string; ctx: HzCtx; onClose: () => void }> = ({ id, ctx, onClose }) => {
  const res = useAdminGet(S, 'result', { id }, [id]);
  const d = res.data || {};
  const r: any = d.result && typeof d.result === 'object' ? d.result : d.row && typeof d.row === 'object' ? d.row : d;
  const prompt: any = d.prompt ?? r.prompt ?? null;
  const run: any = d.run ?? r.run ?? null;
  const p = ctx.promptById(r.prompt_id);
  const levers = leversOf(r.value_lever_tags ?? r.levers);
  const apps = appsOf(r.example_applications ?? r.applications);
  const techId = str(r.utech_technology_id || r.tech_id);
  const other = Object.entries(r).filter(([k]) => !SHOWN.has(k));

  return (
    <Drawer onClose={onClose} z="z-50" width="56rem" label={`Horizon result ${id}`}
      header={
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Pill tone="neutral">Result</Pill>
            <span className="text-[11px] font-mono text-grayscale-1100 break-all">{id}</span>
            {str(r.error) && <Pill tone="bad">error</Pill>}
            {r.is_current === true && <Pill tone="good" title="The result the public tech page shows">current</Pill>}
            {r.ok === false && !str(r.error) && <Pill tone="warn" title="No horizon in the response — never used">not usable</Pill>}
            {r.tech_in_corpus === false && <Pill tone="warn">technology no longer in the corpus</Pill>}
          </div>
          <div className="text-base font-bold text-grayscale-1300 font-grotesk break-words">{str(r.tech_title) || techId || id}</div>
          <div className="text-xs text-grayscale-1100 break-words">
            {[str(r.university_name), techId, r.created_at ? fmtDate(r.created_at) : ''].filter(Boolean).join(' · ')}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {techId && <Btn small onClick={() => { ctx.openTech(techId); onClose(); }}>Open technology</Btn>}
            {r.run_id && <Btn small onClick={() => ctx.goResults({ run: str(r.run_id) })}>All results of this run</Btn>}
            {r.prompt_id && <Btn small onClick={() => ctx.goPrompt(str(r.prompt_id))}>Prompt</Btn>}
            {r.tech_page_path && (
              <a href={publicUrl(r.tech_page_path)} target="_blank" rel="noopener noreferrer"
                className="text-xs text-primary-700 underline underline-offset-2 whitespace-nowrap">Public page ↗</a>
            )}
          </div>
        </div>
      }>
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {res.loading && !res.data ? <Loading label="Reading the result…" /> : (
        <>
          {str(r.error) && <Msg kind="error">{str(r.error)}</Msg>}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Fact label="Horizon"><HorizonPill h={r.horizon} long /></Fact>
            <Fact label="Confidence"><ConfidencePill c={r.horizon_confidence} /></Fact>
            <Fact label="Industry">{str(r.industry) || '—'}</Fact>
            <Fact label="Primary problem type">{str(r.primary_problem_type) || '—'}</Fact>
            <Fact label="Secondary problem types">{str(r.secondary_problem_types) || '—'}</Fact>
            <Fact label="Tokens">{num(r.tokens).toLocaleString()}</Fact>
            <Fact label="Cost">{fmtCost(r.cost, true)}</Fact>
            <Fact label="Latency">{r.latency_ms ? `${(num(r.latency_ms) / 1000).toFixed(2)}s` : '—'}</Fact>
            <Fact label="Prompt" title={str(r.prompt_id)}>
              <span className="break-words">{p ? promptLabel(p, r.prompt_id) : (str(r.prompt_name) || shortId(r.prompt_id) || '—')}</span>
              {p?.is_final && <> <Pill tone="good">final</Pill></>}
            </Fact>
            <Fact label="Run"><span className="font-mono text-xs break-all">{str(r.run_id) || '—'}</span></Fact>
            <Fact label="Created">{fmtDate(r.created_at)}</Fact>
            <Fact label="Technology id"><span className="font-mono text-xs break-all">{techId || '—'}</span></Fact>
          </div>

          <Section title="Problem & applications">
            <div className="flex flex-col gap-1">
              <div className="text-xs font-bold text-grayscale-1200">Inferred problem</div>
              <p className="text-sm text-grayscale-1300 break-words">{str(r.inferred_problem) || '—'}</p>
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-xs font-bold text-grayscale-1200">Example applications ({apps.length})</div>
              {apps.length ? (
                <ul className="list-disc pl-5 text-sm text-grayscale-1300 flex flex-col gap-0.5">
                  {apps.map((a, i) => <li key={i} className="break-words">{a}</li>)}
                </ul>
              ) : <div className="text-sm text-grayscale-1100">None.</div>}
            </div>
          </Section>

          <Section title={`Value levers → technical tags (${levers.length})`}>
            <LeverChips levers={levers} />
            <div className="grid md:grid-cols-3 gap-2">
              <Fact label="Tags to add">{str(r.tags_to_add) || '—'}</Fact>
              <Fact label="Tags to remove">{str(r.tags_to_remove) || '—'}</Fact>
              <Fact label="Correct tags">{fmtCell('correct_tags', r.correct_tags)}</Fact>
            </div>
            {str(r.value_lever_tags) && typeof r.value_lever_tags === 'string' && (
              <details className="text-xs">
                <summary className="cursor-pointer text-grayscale-1200">As stored</summary>
                <TextBlock text={r.value_lever_tags} maxHeight="10rem" />
              </details>
            )}
          </Section>

          {str(r.reasoning) && (
            <Section title="Reasoning">
              <p className="text-sm text-grayscale-1300 whitespace-pre-wrap break-words">{str(r.reasoning)}</p>
            </Section>
          )}

          <Section title="Review">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Fact label="Reviewed">{yn(r.reviewed)}</Fact>
              <Fact label="Approved">{yn(r.approved)}</Fact>
              <Fact label="Horizon correct">{yn(r.correct_horizon)}</Fact>
              <Fact label="Problem type correct">{yn(r.correct_problem_type)}</Fact>
              <Fact label="Corrected horizon">{str(r.corrected_horizon) || '—'}</Fact>
              <Fact label="Corrected problem type">{str(r.corrected_problem_type) || '—'}</Fact>
              <Fact label="Reviewed at">{r.reviewed_at ? fmtDate(r.reviewed_at) : '—'}</Fact>
              <Fact label="Notes">{str(r.review_notes) || '—'}</Fact>
            </div>
          </Section>

          {other.length > 0 && (
            <Section title="Other columns">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {other.map(([k, v]) => <Fact key={k} label={k.replace(/_/g, ' ')}>{fmtCell(k, v)}</Fact>)}
              </div>
            </Section>
          )}

          <Section title="Raw model response">
            <TextBlock text={r.raw_response} mono maxHeight="32rem" empty="No raw response stored for this result." />
          </Section>

          {(run && typeof run === 'object') && (
            <Section title="Run">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Fact label="Run id"><span className="font-mono text-xs break-all">{str(run.id)}</span></Fact>
                <Fact label="Model">{str(run.model) || '—'}</Fact>
                <Fact label="Sample size">{run.sample_size !== undefined ? num(run.sample_size).toLocaleString() : '—'}</Fact>
                <Fact label="Started">{fmtDate(run.created_at)}</Fact>
                <Fact label="Tokens">{run.total_tokens !== undefined ? num(run.total_tokens).toLocaleString() : '—'}</Fact>
                <Fact label="Cost">{fmtCost(run.total_cost)}</Fact>
                <Fact label="Prompt name">{str(run.prompt_name) || '—'}</Fact>
                <Fact label="Filters"><span className="text-xs break-words">{typeof run.filters === 'string' ? run.filters : JSON.stringify(run.filters ?? '')}</span></Fact>
              </div>
            </Section>
          )}

          {(prompt && typeof prompt === 'object') && (
            <Section title={`Prompt — ${str(prompt.name) || shortId(prompt.id)}`}>
              <div className="text-[11px] text-grayscale-1100 break-all">
                <span className="font-mono">{str(prompt.id)}</span>{prompt.created_at ? ` · ${fmtDate(prompt.created_at)}` : ''}
              </div>
              {(['master_prompt', 'horizon_md', 'problem_md'] as const).map(k => (str(prompt[k]) ? (
                <details key={k} className="text-xs">
                  <summary className="cursor-pointer text-grayscale-1200 font-bold">{k} ({str(prompt[k]).length.toLocaleString()} chars)</summary>
                  <div className="mt-1"><TextBlock text={prompt[k]} mono maxHeight="24rem" /></div>
                </details>
              ) : null))}
              <div><Btn small onClick={() => ctx.goPrompt(str(prompt.id || r.prompt_id))}>Open in Prompts →</Btn></div>
            </Section>
          )}
        </>
      )}
    </Drawer>
  );
};

export default ResultDrawer;
